import PropTypes from 'prop-types';

import { IconAddCircle, IconContinue, IconSearchField } from '../../../Components/Common/Icons';
import { useLanguage } from '../../../localization/useLanguage';
import { cn } from '../../../ui/lib/utils';
import ContactPicker, { SelectionChips } from '../ContactPicker';
import { SEARCH_MAX_LENGTH } from '../constants';
import dialogStyles from '../../../Components/WebChat/Common/ContactList/dialog.module.scss';
import styles from './index.module.scss';

/**
 * New Group pop-up, step 1 (FR60 screen 60.3): choose the members.
 *
 * @param {object} search useContactSearch state for this step.
 * @param {Map} selected Selected contacts keyed by userId.
 * @param {Function} onToggle Selects or deselects a contact.
 * @param {Function} onNewContact Opens the New Contact pop-up.
 * @param {Function} onContinue Goes to the group details step.
 */
const MembersStep = ({ search, selected, onToggle, onNewContact, onContinue }) => {
    const { t } = useLanguage();
    const isEmpty = search.hasLoaded && !search.isLoading && search.contacts.length === 0;

    return (
        <>
            <div className={dialogStyles.search}>
                <span className={dialogStyles.searchIcon}>
                    <IconSearchField />
                </span>
                <input
                    type="search"
                    value={search.inputValue}
                    maxLength={SEARCH_MAX_LENGTH}
                    placeholder={t('createChat.SEARCH_CONTACTS')}
                    aria-label={t('createChat.SEARCH_CONTACTS')}
                    onChange={event => search.onInputChange(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && search.onSubmit()}
                />
            </div>

            <button type="button" className={dialogStyles.addContact} onClick={onNewContact}>
                <span className={dialogStyles.addContactBadge}>
                    <IconAddCircle />
                </span>
                {t('createChat.NEW_CONTACT')}
            </button>

            <p className={dialogStyles.sectionCaption}>{t('createChat.YOUR_CONTACTS')}</p>
            <ContactPicker
                contacts={search.contacts}
                selected={selected}
                onToggle={onToggle}
                onLoadMore={search.onLoadMore}
                isEmpty={isEmpty}
            />
            <SelectionChips selected={selected} onRemove={onToggle} />

            <button
                type="button"
                className={cn(dialogStyles.button, dialogStyles.primary, dialogStyles.block, styles.continue)}
                disabled={selected.size === 0}
                onClick={onContinue}
            >
                {t('createChat.CONTINUE')}
                <IconContinue className={styles.continueIcon} />
            </button>
        </>
    );
};

MembersStep.propTypes = {
    search: PropTypes.object.isRequired,
    selected: PropTypes.instanceOf(Map).isRequired,
    onToggle: PropTypes.func.isRequired,
    onNewContact: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
};

export default MembersStep;
